"use client";

import React, { useEffect, useState } from "react";
import { signOut, useSession } from "next-auth/react";

type GenerateResponse = { roomId?: string; joinUrl: string; error?: string };

export default function CreatePage() {
  const { data: session, status } = useSession();
  const isAdmin = !!(session?.user as any)?.isAdmin;

  const [origin, setOrigin] = useState("");
  const [roomId, setRoomId] = useState<string>("");
  const [joinUrl, setJoinUrl] = useState<string>("");
  const [msg, setMsg] = useState<string>("");

  useEffect(() => {
    // Avoid hydration mismatch: window/crypto only on client
    setOrigin(window.location.origin);
    setRoomId(crypto.randomUUID().slice(0, 8) + "-" + crypto.randomUUID().slice(0, 4));
  }, []);

  async function generateInvite() {
    setMsg("");
    setJoinUrl("");

    const roomSlug = roomId.trim();
    if (!roomSlug) {
      setMsg("❌ Room ID is empty.");
      return;
    }

    try {
      const res = await fetch("/api/invite?roomId=" + encodeURIComponent(roomSlug), { method: "GET" });

      if (!res.ok) {
        const t = await res.text();
        setMsg(`❌ Invite failed (${res.status}): ${t}`);
        return;
      }

      const data = (await res.json()) as GenerateResponse;
      setJoinUrl(data.joinUrl);
      setMsg("✅ Invite generated.");
    } catch (e: any) {
      setMsg(`❌ Invite failed: ${e?.message || String(e)}`);
    }
  }

  async function copyLink() {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setMsg("✅ Copied to clipboard.");
    } catch {
      setMsg("❌ Clipboard failed. Copy manually.");
    }
  }

  return (
    <main style={{ padding: 18, maxWidth: 920, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900 }}>Session</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {status === "loading"
              ? "Checking session…"
              : session?.user?.email
              ? `Signed in as ${session.user.email}`
              : "Not signed in"}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {session?.user ? (
  <>
    {isAdmin ? (
      <a href="/admin" style={{ textDecoration: "underline", fontWeight: 700 }}>
        Admin
      </a>
    ) : null}
    <button onClick={() => signOut({ callbackUrl: "/login" })} style={btnLight}>
      Log out
    </button>
  </>
) : (
  <a href="/login" style={{ textDecoration: "underline", fontWeight: 700 }}>
    Login
  </a>
)}
        </div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Create a room invite</h2>

          <label style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 700 }}>Room ID</div>
            <input
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="e.g. b423492a-2053"
              style={input}
            />
          </label>

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={generateInvite} style={btnDark}>
              Generate Invite
            </button>
            <button
              onClick={() => setRoomId(crypto.randomUUID().slice(0, 8) + "-" + crypto.randomUUID().slice(0, 4))}
              style={btnLight}
            >
              New Room ID
            </button>
          </div>

          {msg ? (
            <div style={{ marginTop: 12, fontWeight: 800, color: msg.includes("✅") ? "#16a34a" : "#b91c1c" }}>
              {msg}
            </div>
          ) : null}

          <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
            Origin: {origin || "—"}
          </div>
        </div>

        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Invite link</h2>

          {joinUrl ? (
            <>
              <textarea readOnly value={joinUrl} style={ta} />
              <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button onClick={copyLink} style={btnDark}>
                  Copy
                </button>
                <button onClick={() => (window.location.href = joinUrl)} style={btnLight}>
                  Open
                </button>
              </div>

              <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>
                Share this link with the other participant(s). They must be approved to join.
              </div>
            </>
          ) : (
            <div style={{ color: "#6b7280" }}>Generate an invite to see a shareable link.</div>
          )}
        </div>
      </div>
    </main>
  );
}

const card: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  background: "white",
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
};

const ta: React.CSSProperties = {
  width: "100%",
  minHeight: 72,
  padding: 10,
  borderRadius: 10,
  border: "1px solid #d1d5db",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
};

const btnDark: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  background: "#111827",
  color: "white",
  cursor: "pointer",
};

const btnLight: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  background: "#f3f4f6",
  color: "#111827",
  cursor: "pointer",
};
